<template>
	<div class="app-error">
		<div class="app-error__card">
			<h1 class="app-error__code">{{ error.statusCode || 'Error' }}</h1>
			<p class="app-error__message">{{ friendlyMessage }}</p>
			<div class="app-error__actions">
				<button
					class="app-error__btn app-error__btn--primary"
					type="button"
					@click="reload"
				>
					Reload
				</button>
				<button
					class="app-error__btn"
					type="button"
					@click="goHome"
				>
					Go to start
				</button>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { NuxtError } from '#app';

const props = defineProps<{ error: NuxtError }>();

// This is the last-resort boundary Nuxt renders when a route/render throws fatally.
// Log it as fatal through an independent span carrying base context (we don't rely on
// the ambient root logger having initialised, since a fatal boot error may pre-empt it).
try {
	const log = useAppLogger({ surface: 'error-boundary' });
	log.fatal('fatal render error', {
		statusCode: props.error?.statusCode,
		statusMessage: props.error?.statusMessage,
		message: props.error?.message,
		route: import.meta.client ? window.location.pathname : undefined,
		stack: props.error?.stack,
		uncaught: true,
		boundary: true,
	});
} catch {
	// Never let the error page itself throw.
}

const friendlyMessage = computed(() => {
	const code = props.error?.statusCode;
	if (code === 404) return 'The page you are looking for could not be found.';
	return 'Something went wrong. Reloading usually fixes it.';
});

function reload() {
	if (import.meta.client) window.location.reload();
}

// clearError resets Nuxt's error state and navigates away from the broken route.
function goHome() {
	clearError({ redirect: '/' });
}
</script>

<style scoped>
.app-error {
	position: fixed;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 1.5rem;
	background: var(--background-25, #fafafa);
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.app-error__card {
	max-width: 26rem;
	width: 100%;
	text-align: center;
}

.app-error__code {
	margin: 0;
	font-size: 3.5rem;
	font-weight: 700;
	color: var(--text-50, #161616);
}

.app-error__message {
	margin: 0.5rem 0 1.75rem;
	font-size: 1rem;
	color: var(--text-50, #262626);
	opacity: 0.75;
}

.app-error__actions {
	display: flex;
	gap: 0.75rem;
	justify-content: center;
	flex-wrap: wrap;
}

.app-error__btn {
	appearance: none;
	border: 1px solid var(--background-100, #e0e0e0);
	background: transparent;
	color: var(--text-50, #262626);
	padding: 0.6rem 1.25rem;
	border-radius: 0.5rem;
	font-size: 0.95rem;
	cursor: pointer;
}

.app-error__btn--primary {
	background: #3498db;
	border-color: #3498db;
	color: #fff;
}
</style>
